import { CONSUMER_BATCH_SIZE, CONSUMER_INTERVAL_MS, MAX_CONSECUTIVE_FAILURES } from './constants';
import { nullishThrows } from './helpers';
import { countJournalEntries, deleteJournalEntries, readJournalEntries } from './journal-idb-reader';
import type { JournaledEventsConsumerDelegate, SyncEvent } from './types';
export type EventJournalConsumerOptions = {
  batchSize?: number;
  intervalMs?: number;
};
export class EventJournalConsumer {
  private taskID: string | null = null;
  private readonly delegate: JournaledEventsConsumerDelegate;
  private readonly batchSize: number;
  private readonly intervalMs: number;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private isOperational = false;
  private isConsumeInFlight = false;
  private pendingCount = 0;
  private consecutiveFailures = 0;
  constructor(delegate: JournaledEventsConsumerDelegate, options?: EventJournalConsumerOptions) {
    this.delegate = delegate;
    this.batchSize = options?.batchSize ?? CONSUMER_BATCH_SIZE;
    this.intervalMs = options?.intervalMs ?? CONSUMER_INTERVAL_MS;
  }
  runForTaskID(taskID: string) {
    this.taskID = taskID;
    this.run();
    return this;
  }
  start(): void {
    if (this.isOperational) return;
    this.isOperational = true;
    this.scheduleNext();
  }
  stop(): void {
    this.isOperational = false;
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  run(): void {
    if (this.isOperational) {
      return;
    }
    this.isOperational = true;
    this.consecutiveFailures = 0;
    this.scheduleNext();
  }
  private scheduleNext(): void {
    if (!this.isOperational) {
      return; // out of service. Paranoid guard
    }
    if (this.timeoutId !== null) {
      return; // A batch is already scheduled
    }
    this.timeoutId = setTimeout(async () => {
      if (!this.isOperational) {
        return;
      }
      try {
        await this.consumeBatch();
      } catch {
        console.error('Batch consumption failed');
      } finally {
        this.timeoutId = null;
        if (this.isOperational) {
          this.scheduleNext();
        }
      }
    }, this.intervalMs);
  }
  async consumeAll(): Promise<void> {
    let hasMore = true;
    while (hasMore) {
      const consumed = await this.consumeBatch();
      hasMore = consumed > 0;
    }
  }
  async getPendingCount(): Promise<number> {
    const taskID = nullishThrows(this.taskID, 'Task ID for JournalConsumer not set!');
    this.pendingCount = await countJournalEntries(taskID);
    return this.pendingCount;
  }
  dispose(): void {
    this.stop();
  }
  private async consumeBatch(): Promise<number> {
    if (!this.taskID || !this.isOperational || this.isConsumeInFlight) {
      return 0;
    }
    try {
      this.isConsumeInFlight = true;
      const entries = await readJournalEntries(this.taskID, this.batchSize);
      if (entries.length === 0) {
        return 0;
      }
      const events: SyncEvent[] = entries.map(e => e.event);
      const retain = await this.delegate.consume(events);
      const toDelete = entries.filter(e => !retain.has(e.objectKey));
      const sequences = toDelete.map(e => e.sequence);
      await deleteJournalEntries(sequences);
      this.consecutiveFailures = 0;
      return entries.length;
    } catch {
      this.consecutiveFailures++;
      if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        this.stop();
        console.error('[EventJournalConsumer] Stopping after too many consecutive failures');
      }
      return 0;
    } finally {
      this.isConsumeInFlight = false;
    }
  }
}